// The labels layer's words (#1118): enum wording (game status, competition
// progress, format, bracket status, stats scope, round names) and the shared
// page strings that buildUi() resolves per sport. Read through useLabels(),
// never directly. Values follow audit TERMINOLOGY.md §2a-2e.
//
// Where the noun changes with the sport (Game / Match), the whole sentence is
// one message function of the sport id, with select(): the sport packs keep
// those nouns in step (test/site/labels/terms.sync.test.ts).
import { select } from '../../../plural'

const en = {
  /** GameStatusKind → fan words. `final` is the pack's term, not here. */
  gameStatus: {
    upcoming: 'Upcoming',
    live: 'Live',
    /** A live game whose league has live updates off. */
    liveNoUpdates: 'In Progress',
    postponed: 'Postponed',
    cancelled: 'Cancelled',
    /** Start time passed, no score yet. */
    scoreSoon: 'Score coming soon',
    dateTba: 'Date TBA',
  },
  /** The competition's progress pill. */
  progress: {
    startsOn: (date: string) => `Starts ${date}`,
    upcoming: 'Upcoming',
    inProgress: 'In progress',
    completed: 'Complete',
  },
  format: {
    roundRobin: 'Round robin',
    bracket: 'Bracket',
  },
  /** A team's status in a bracket (classic adds its own trophy). */
  bracketStatus: {
    alive: 'Still in',
    eliminated: 'Eliminated',
    champion: 'Champion',
  },
  /** Stats-scope tabs; players and teams word the career scope differently. */
  statsView: {
    player: { lifetime: 'Career', season: 'Season', competition: 'Competition' },
    team: { lifetime: 'All-time', season: 'Season', competition: 'Competition' },
  },
  /** Bracket round names, from the rounds left. */
  round: {
    playIn: 'Play-in',
    final: 'Final',
    semifinals: 'Semifinals',
    quarterfinals: 'Quarterfinals',
    numbered: (n: number) => `Round ${n}`,
  },

  // ── page strings (UiStrings) ────────────────────────────────────────────
  schedule: {
    emptyTitle: 'Nothing scheduled yet',
    emptyBody: (sport: string) =>
      select(sport, {
        soccer: 'Check back soon for matches, brackets and standings.',
        other: 'Check back soon for games, brackets and standings.',
      }),
    subtitle: (sport: string) =>
      select(sport, {
        soccer: 'Matches, brackets and standings for every competition',
        other: 'Games, brackets and standings for every competition',
      }),
    loadError: "We couldn't load the schedule.",
    competitionLoadError: "We couldn't load this competition.",
    competitionNotFound: "We couldn't find that competition",
    competitionNotFoundBody: "It may have ended, or it isn't public yet.",
    liveNowHeading: 'Live now',
    roundRobinEmptyTitle: 'Schedule Coming Soon',
    roundRobinEmptyBody: (sport: string) =>
      select(sport, {
        soccer: "Matches for this competition haven't been scheduled yet.",
        other: "Games for this competition haven't been scheduled yet.",
      }),
    matchday: (n: number | string) => `Matchday ${n}`,
    gameDetailsLink: (sport: string) => select(sport, { soccer: 'Match details', other: 'Game details' }),
    bracketEmptyTitle: 'Bracket Coming Soon',
    bracketEmptyBody: "The bracket for this competition hasn't been set yet.",
    /** #652: the season picker (a sport with 2+ public seasons). `current` is today's view (the active season or seasons). */
    seasonPicker: 'Season',
    currentSeason: 'Current season',
    currentSeasons: 'Current seasons',
  },
  home: {
    /** PAIR: admin-v2/data/site/design.ts defaultTitle. */
    upcomingTitle: (sport: string) => select(sport, { soccer: 'Upcoming matches', other: 'Upcoming games' }),
    upcomingEmpty: (sport: string) => select(sport, { soccer: 'No upcoming matches yet', other: 'No upcoming games yet' }),
    streamLive: 'Watch live',
    streamed: 'Streamed',
  },
  game: {
    notFoundTitle: (sport: string) => select(sport, { soccer: 'Match not found', other: 'Game not found' }),
    notFoundBody: (sport: string) => select(sport, { soccer: "We couldn't find this match.", other: "We couldn't find this game." }),
    sponsoredBy: (sport: string) => select(sport, { soccer: 'Match sponsor', other: 'Game sponsor' }),
    noBoxScore: (sport: string) =>
      select(sport, { soccer: 'No box score for this match yet.', other: 'No box score for this game yet.' }),
    notStartedTitle: (sport: string) => select(sport, { soccer: "Match hasn't started", other: "Game hasn't started" }),
    /** The game page's name in new templates. */
    centerTitle: (sport: string) => select(sport, { soccer: 'Match center', other: 'Game center' }),
    shareTitle: 'Share result',
    scorebookTitle: 'Live stats',
    scorebookSubtitle: 'Powered by ScoreLM Scorebook',
  },
  /** Player and team pages share these. */
  record: {
    playerShareLifetime: 'Career stats',
    teamShareLifetime: 'All-time stats',
    shareSeasonFallback: 'Season Stats',
    shareCompetitionFallback: 'Competition stats',
    /** "{season or competition name} Stats" */
    shareNamed: (name: string) => `${name} Stats`,
    competitionSelectPlaceholder: 'Choose a competition…',
    competitionPick: 'Choose a competition to see stats',
  },
  player: {
    emptyLifetime: 'No stats available for this player',
    seasonPick: 'Select a season to view stats',
    seasonEmpty: 'No stats for selected season',
    competitionEmpty: 'No stats in this competition yet',
    gameLogTitle: (sport: string) => select(sport, { soccer: 'Match log', other: 'Game log' }),
    gameLogAllScope: (sport: string) => select(sport, { soccer: 'All matches', other: 'All games' }),
  },
  stats: {
    allTime: 'All-time',
    allCompetitions: 'All competitions',
    minGamesPlayed: (sport: string) => select(sport, { soccer: 'Min MP', other: 'Min GP' }),
    noLeader: 'No leader yet',
    totalsToggle: 'Totals',
  },
  gallery: {
    intro: (tenantName: string, sport: string) =>
      select(sport, {
        soccer: `Photos from ${tenantName} matches and events`,
        other: `Photos from ${tenantName} games and events`,
      }),
  },
  sale: {
    competitionLink: 'View competition →',
    gameLink: (sport: string) => select(sport, { soccer: 'View match →', other: 'View game →' }),
  },
  venue: {
    spacesHeading: 'Spaces at this venue',
    gamesHeading: (sport: string) => select(sport, { soccer: 'Matches at this venue', other: 'Games at this venue' }),
    gamesEmpty: (sport: string) => select(sport, { soccer: 'No matches here yet.', other: 'No games here yet.' }),
    venuesSubtitle: (sport: string) => select(sport, { soccer: 'Where the matches happen', other: 'Where the games happen' }),
  },
  /** Relative-day words for upcoming games. */
  when: {
    tonight: 'Tonight',
    today: 'Today',
    tomorrow: 'Tomorrow',
    thisWeek: 'This week',
  },
}

export default en
